import { useCallback, useEffect, useRef, useState } from 'react';
import { runQuery } from '../../services/dashboards';

const MIN_HEIGHT = 320;
const MAX_HEIGHT = 20000;

const REFRESH_MS = {
  off: 0,
  '30s': 30e3,
  '1m': 60e3,
  '5m': 300e3,
  '15m': 900e3,
  '1h': 3600e3,
};

// A stable module-level reference, not an inline default: an inline arrow
// default is a fresh function on every render, and it sits in the
// message-listener effect's dependency array, so a fresh one would tear the
// listener down and abortAll() every in-flight query on every re-render
// (e.g. the setBusy update a query itself triggers).
const defaultResolveQuery = (name, decl, tr, signal) => runQuery(decl, tr, signal);

/** The API's failure translated into the fixed vocabulary the SDK understands. */
const codeFor = (e) => {
  if (!e) return 'upstream_error';
  if (e.status === 403) return 'forbidden';
  if (e.status === 404) return 'device_not_found';
  if (e.status === 429) return 'rate_limited';
  return 'upstream_error';
};

/**
 * The host half of the dashboard bridge.
 *
 * Everything here runs with the viewer's session and takes input from HTML the
 * viewer did not write. Treat every message as hostile: check the window it
 * came from, check that the sandbox is still intact, and authorize names
 * against the manifest the API returned — never the one the frame sent.
 */
/**
 * The query resolver is a parameter so a shared view can supply a
 * token-authenticated variant without the hook growing a second branch. The
 * authorization above it — the own-property check against the manifest the
 * API returned — stays one code path for both, which is the point: it is the
 * security boundary, and a boundary with two implementations has two chances
 * to be wrong.
 *
 * @param {(name:string, decl:Object, timeRange:string, signal:AbortSignal) => Promise<any>} resolveQuery
 */
export function useZafronBridge({
  frameRef,
  dashboard,
  timeRange,
  theme,
  resolveQuery = defaultResolveQuery,
  shared = false,
}) {
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(0);
  const [height, setHeight] = useState(MIN_HEIGHT);
  const [frameError, setFrameError] = useState(null);
  const inflight = useRef(new Map());

  /**
   * targetOrigin '*' is required, not lax: the sandboxed frame has an opaque
   * origin, so a targeted post is silently dropped and the dashboard never
   * receives anything. The SDK authenticates us by our origin on its side.
   */
  const send = useCallback((msg) => {
    const w = frameRef.current && frameRef.current.contentWindow;
    if (w) w.postMessage({ ...msg, z: 1 }, '*');
  }, [frameRef]);

  const abortAll = useCallback(() => {
    inflight.current.forEach((ac) => ac.abort());
    inflight.current.clear();
  }, []);

  // The latest context lives in a ref that every render rewrites, so the
  // listener registers once and can never close over a stale range.
  const ctx = useRef({ timeRange, theme, dashboard });
  ctx.current = { timeRange, theme, dashboard };

  useEffect(() => {
    async function onMessage(event) {
      const frame = frameRef.current;
      if (!frame || event.source !== frame.contentWindow) return;
      // Not authentication — window identity above is that. This asserts the
      // sandbox is intact: a real origin means allow-same-origin was added,
      // and the dashboard must visibly stop working rather than quietly
      // gaining access to the viewer's session.
      if (event.origin !== 'null') return;

      const msg = event.data;
      if (!msg || msg.z !== 1) return;

      const { dashboard: dash, timeRange: tr, theme: th } = ctx.current;
      if (!dash) return;

      if (msg.type === 'ready') {
        setReady(true);
        send({
          type: 'init',
          theme: th,
          timeRange: tr,
          granted: Object.keys((dash.manifest && dash.manifest.queries) || {}),
          viewer: {
            locale: navigator.language,
            tzOffsetMinutes: -new Date().getTimezoneOffset(),
            shared,
          },
        });
        return;
      }

      if (msg.type === 'query') {
        const queries = (dash.manifest && dash.manifest.queries) || {};
        // Object.prototype.hasOwnProperty guards a lookup keyed by frame-chosen
        // input: a plain `queries[msg.name]` walks the prototype chain, so names
        // like "__proto__" or "constructor" resolve to something truthy and skip
        // the unknown_query refusal, reaching the network with no manifest entry
        // behind them.
        const decl = typeof msg.name === 'string'
          && Object.prototype.hasOwnProperty.call(queries, msg.name)
          ? queries[msg.name]
          : null;
        if (!decl) {
          send({
            type: 'result',
            id: msg.id,
            ok: false,
            error: 'unknown_query',
            detail: `"${msg.name}" is not a query in this dashboard.`,
          });
          return;
        }

        const ac = new AbortController();
        // The frame chooses msg.id; a reused id must not orphan the previous
        // controller — abort it first so abortAll can still reach every live
        // request and its own finally below won't delete this one's entry.
        const prev = inflight.current.get(msg.id);
        if (prev) prev.abort();
        inflight.current.set(msg.id, ac);
        setBusy((n) => n + 1);
        try {
          const data = await resolveQuery(msg.name, decl, tr, ac.signal);
          if (!ac.signal.aborted) send({ type: 'result', id: msg.id, ok: true, data });
        } catch (e) {
          // A superseded or torn-down query answers with nothing: the asker
          // is gone.
          if (ac.signal.aborted || (e && e.name === 'AbortError')) return;
          send({
            type: 'result',
            id: msg.id,
            ok: false,
            error: codeFor(e),
            detail: (e && e.message) || 'The request failed.',
          });
        } finally {
          // Only delete this controller's own entry: a superseded query's
          // finally must not delete the entry a later reuse of the same id
          // already installed.
          if (inflight.current.get(msg.id) === ac) inflight.current.delete(msg.id);
          setBusy((n) => Math.max(0, n - 1));
        }
        return;
      }

      if (msg.type === 'resize') {
        // Applied exactly, never padded. The frame measures itself inside the
        // box this sets, so slack added here comes back in its next report
        // and the frame grows by that slack every tick, without end.
        if (Number.isFinite(msg.height)) {
          setHeight(Math.max(MIN_HEIGHT, Math.min(Math.ceil(msg.height), MAX_HEIGHT)));
        }
        return;
      }

      if (msg.type === 'error') {
        // msg.message is frame-controlled and unbounded; React escapes it so
        // this isn't XSS, but an unbounded string posted in a loop lands in
        // state and the DOM on every render and makes the host page janky.
        setFrameError(String(msg.message || 'The dashboard threw an error.').slice(0, 500));
      }
    }

    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
      abortAll();
    };
  }, [frameRef, send, abortAll, resolveQuery, shared]);

  // Context changes push down; the frame decides what to re-query. `init`
  // already carries the current timeRange and theme, so the render where
  // `ready` flips false->true must not also push a redundant context: that
  // would run the dashboard's listeners a second (and, split across two
  // effects, third) time on every boot. wasReady tracks the previous value
  // so only an actual change after boot sends anything, and both context
  // values live in one effect so a real change sends exactly one message.
  const wasReady = useRef(false);
  useEffect(() => {
    const justBooted = ready && !wasReady.current;
    wasReady.current = ready;
    if (ready && !justBooted) send({ type: 'context', timeRange, theme });
  }, [ready, timeRange, theme, send]);

  // Auto-refresh on the manifest's cadence, floored at 30s no matter what it asks for.
  const period = REFRESH_MS[(dashboard && dashboard.manifest && dashboard.manifest.refresh) || 'off'] || 0;
  const dashboardId = dashboard && dashboard.id;
  useEffect(() => {
    if (!ready || !period) return undefined;
    const t = setInterval(() => send({ type: 'context', refresh: true }), Math.max(period, 30e3));
    return () => clearInterval(t);
  }, [ready, period, send]);

  const refresh = useCallback(() => send({ type: 'context', refresh: true }), [send]);
  const dismissError = useCallback(() => setFrameError(null), []);

  // Shared by both places that discard the current document's state: nothing
  // it said (its readiness, its error, its last-reported height) still
  // applies once that document is gone.
  const resetForNewDocument = useCallback(() => {
    setReady(false);
    setFrameError(null);
    setHeight(MIN_HEIGHT);
    abortAll();
  }, [abortAll]);

  const loads = useRef(0);
  const onFrameLoad = useCallback(() => {
    loads.current += 1;
    // The first load is the dashboard booting. A later one means the frame
    // navigated or reloaded: the document there is no longer the one that
    // asked the outstanding questions, and the host must not keep its stale
    // height or a stale error strip either.
    if (loads.current > 1) {
      resetForNewDocument();
    }
  }, [resetForNewDocument]);

  // A different dashboard id means a different frame behind the same src
  // swap: none of the previous one's state still applies. Nothing the old
  // frame said carries over, and any query it's still waiting on must not
  // resolve into the new one. This also fires on mount, which is harmless —
  // every value it sets is already at its initial value then.
  useEffect(() => {
    resetForNewDocument();
    loads.current = 0;
  }, [dashboardId, resetForNewDocument]);

  return { ready, busy, height, frameError, dismissError, refresh, onFrameLoad };
}
